/**
 * Org passport — the sticky identity band for the redesigned org page.
 *
 * Collects everything that identifies the org (name, status, the three IDs
 * admins constantly copy) and every org-level action into one calm strip, so
 * the rest of the page can be pure content.
 */

import { useState } from 'react';
import { Business, Edit, KeyboardArrowDown, OpenInNew, Sync } from '@mui/icons-material';
import { Button, Chip, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';

import { IdChip } from './primitives';
import type { OrgIdentity, SubscriptionState } from './types';

const STATE_LABEL: Record<SubscriptionState, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past due',
  paused: 'Paused',
  canceled: 'Canceled',
  pending_cancellation: 'Cancels at period end',
  none: 'No subscription',
};

const STATE_COLOR: Record<SubscriptionState, 'success' | 'info' | 'warning' | 'error' | 'default'> =
  {
    active: 'success',
    trialing: 'info',
    past_due: 'warning',
    paused: 'warning',
    canceled: 'error',
    pending_cancellation: 'warning',
    none: 'default',
  };

export interface OrgPassportProps {
  identity: OrgIdentity;
  subscriptionState: SubscriptionState;
  onRename?: () => void;
  onOpenStripe?: () => void;
  onSync?: () => void;
  onMerge?: () => void;
  onCancelSubscription?: () => void;
}

export function OrgPassport({
  identity,
  subscriptionState,
  onRename,
  onOpenStripe,
  onSync,
  onMerge,
  onCancelSubscription,
}: OrgPassportProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <div className="sticky top-0 z-10 -mx-4 border-b border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      {/* Title row: identity + actions. IDs live on their own row below so
          nothing wraps raggedly between the two. */}
      <div className="flex flex-wrap items-center gap-2">
        <Business className="shrink-0 text-gray-400" fontSize="small" />
        <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {identity.name}
        </Typography>
        {onRename && (
          <Tooltip title="Rename organization">
            <IconButton size="small" onClick={onRename}>
              <Edit sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <Chip
          label={STATE_LABEL[subscriptionState]}
          color={STATE_COLOR[subscriptionState]}
          size="small"
        />

        <div className="ml-auto flex items-center gap-1.5">
          {onOpenStripe && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={onOpenStripe}
            >
              Stripe
            </Button>
          )}
          {onSync && (
            <Button size="small" variant="outlined" startIcon={<Sync />} onClick={onSync}>
              Sync
            </Button>
          )}
          {/* Every menu entry is conditional, so the button only renders
              when there is at least one action to offer. */}
          {(onMerge || onCancelSubscription) && (
            <>
              <Button
                size="small"
                variant="outlined"
                endIcon={<KeyboardArrowDown />}
                onClick={e => setMenuAnchor(e.currentTarget)}
              >
                Actions
              </Button>
              <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                {onMerge && (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      onMerge();
                    }}
                  >
                    Merge another org into this one
                  </MenuItem>
                )}
                {onCancelSubscription && (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      onCancelSubscription();
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    Cancel subscription…
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </div>
      </div>

      {/* Dedicated IDs row — the reference material admins copy, in one
          predictable slot. */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <IdChip label="org" value={identity.organizationId} />
        {identity.stripeCustomerId && <IdChip label="cus" value={identity.stripeCustomerId} />}
        {identity.stripeSubscriptionId && (
          <IdChip label="sub" value={identity.stripeSubscriptionId} />
        )}
        {identity.dosespotClinicId && (
          <IdChip label="rx clinic" value={identity.dosespotClinicId} />
        )}
        {identity.createdAt && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Customer since{' '}
            {new Date(identity.createdAt).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
            })}
          </Typography>
        )}
      </div>
    </div>
  );
}
